<script setup lang="ts">
  import { testIds } from '@gat-ship/types'
  import type { Stakeholder } from '@gat-ship/types'
  import { IconLoading } from '@gat-ship/icons'
  import {
    useAppLoading,
    type ButtonProps,
    Button
  } from '@gat-ship/vue-components'
  import { computed, ref } from 'vue'
  import { useStakeholderContext } from '../../composables/useStakeholder'
  import { useStakeholderPermissions } from '@gat-ship/stakeholders'
  import { text } from '@gat-ship/stakeholders'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@gat-ship/vue-components'

  const props = defineProps<{ stakeHolder: Stakeholder }>()
  const { canRestore } = useStakeholderPermissions()

  const { isLoading: appIsLoading } = useAppLoading()
  const service = useStakeholderContext()
  const emit = defineEmits<{
    (e: 'saving'): void
    (e: 'restored'): void
  }>()

  const isLoading = computed(() => appIsLoading(props.stakeHolder.id))
  const showConfirm = ref(false)
  const confirmText = computed(
    () =>
      text.groups.restoreStakeholder(props.stakeHolder.stakeholderType)
        .confirmDialog
  )
  const btnConfig = computed(
    () =>
      text.groups.restoreStakeholder(props.stakeHolder.stakeholderType).buttons
        .restoreStakeholder as {
        label: string
        variant?: ButtonProps['variant']
        size?: ButtonProps['size']
        icon?: unknown
        class?: string
      }
  )

  async function onRestoreStakeholder() {
    showConfirm.value = false
    emit('saving')
    await service?.commands.restore(props.stakeHolder.id)
    emit('restored')
  }
</script>

<template>
  <Button
    v-if="canRestore(props.stakeHolder)"
    :variant="btnConfig.variant"
    :size="btnConfig.size"
    :class="btnConfig.class"
    :disabled="isLoading"
    :data-testid="testIds.stakeholders.actions.restoreStakeholder"
    @click="showConfirm = true"
  >
    <IconLoading v-if="isLoading" class="size-4 shrink-0" />
    <component :is="btnConfig.icon" v-else-if="btnConfig.icon" class="size-4" />
    {{ btnConfig.label }}
  </Button>
  <AlertDialog v-model:open="showConfirm">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ confirmText.title }}</AlertDialogTitle>
        <AlertDialogDescription class="whitespace-pre-line">
          {{ confirmText.message(props.stakeHolder.name) }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <Button variant="outline" @click="onRestoreStakeholder()">
          Confirm
        </Button>
        <Button variant="outline" @click="showConfirm = false"> Cancel </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
